import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Plus, Trash2, Eye, EyeOff, MessageSquare, Sparkles, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Verwaltung der Leser-Fragen für den Blog.
 *
 * Admin kann:
 *  - Frage anlegen (z. B. „Worüber sollen wir als nächstes berichten?")
 *  - Frage als aktiv markieren (im Blog-Banner angezeigt — nur EINE gleichzeitig)
 *  - Alle Antworten pro Frage einsehen
 *  - Frage löschen (Antworten bleiben im Postfach erhalten)
 *
 * Beispiel-Fragen (als Schnell-Vorlagen):
 *  - "Worüber sollen wir als nächstes berichten?"
 *  - "Sag etwas zum Thema: Haustiere"
 */

const TEMPLATES = [
  { q: 'Worüber sollen wir als nächstes berichten?', h: 'Themenvorschläge willkommen' },
  { q: 'Sag etwas zum Thema: Haustiere', h: 'Tier-Storys, Tipps & Lieblinge' },
  { q: 'Was ist dein liebstes Me-Time-Ritual?', h: 'Inspiration für andere' },
  { q: 'Welche Saison-Box gefällt dir am meisten?', h: 'Frühling · Sommer · Herbst · Winter' },
];

const BlogQuestionsManager = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQ, setNewQ] = useState('');
  const [newHint, setNewHint] = useState('');
  const [expandedAnswers, setExpandedAnswers] = useState({}); // qid -> answers[]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/blog-questions');
      setQuestions(r.data || []);
    } catch (e) {
      toast.error('Fragen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createQuestion = async (e) => {
    e?.preventDefault();
    if (!newQ.trim() || newQ.trim().length < 3) {
      toast.error('Frage zu kurz');
      return;
    }
    try {
      await axiosInstance.post('/blog-questions', {
        question: newQ.trim(),
        topic_hint: newHint.trim() || null,
        is_active: true, // direkt aktiv setzen, deaktiviert andere
      });
      toast.success('Frage angelegt – im Blog sichtbar');
      setNewQ('');
      setNewHint('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const toggleActive = async (q) => {
    try {
      await axiosInstance.put(`/blog-questions/${q.id}`, { is_active: !q.is_active });
      toast.success(q.is_active ? 'Deaktiviert' : 'Aktiviert (im Blog sichtbar)');
      load();
    } catch (e) {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const removeQuestion = async (q) => {
    if (!window.confirm(`Frage „${q.question}" wirklich löschen?\n\nDie ${q.answer_count || 0} Antwort(en) bleiben im Postfach erhalten.`)) return;
    try {
      await axiosInstance.delete(`/blog-questions/${q.id}`);
      toast.success('Gelöscht');
      load();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const loadAnswers = async (qid) => {
    if (expandedAnswers[qid]) {
      setExpandedAnswers((prev) => { const next = { ...prev }; delete next[qid]; return next; });
      return;
    }
    try {
      const r = await axiosInstance.get(`/blog-questions/${qid}/answers`);
      setExpandedAnswers((prev) => ({ ...prev, [qid]: r.data || [] }));
    } catch (e) {
      toast.error('Antworten konnten nicht geladen werden');
    }
  };

  const formatTime = (iso) => {
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: de }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen p-3 sm:p-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="blog-questions-page">
      <div className="max-w-4xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-[#5D4E3C] hover:text-[#3D2F22]" data-testid="back-to-admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="p-2.5 bg-[#E8DCC8]">
            <MessageSquare className="h-5 w-5 text-[#5D4E3C]" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#5D4E3C]">Blog-Leserfragen</h1>
            <p className="text-xs sm:text-sm text-[#8B7355]">
              Stelle Fragen an deine Leserschaft – Antworten landen direkt im Postfach
            </p>
          </div>
        </div>

        {/* Neue Frage anlegen */}
        <Card className="rounded-none border-[#D4C4B0]">
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-[#5D4E3C]" />
              <h2 className="font-semibold text-[#5D4E3C]">Neue Frage stellen</h2>
            </div>
            <form onSubmit={createQuestion} className="space-y-3">
              <div>
                <Label htmlFor="newQ" className="text-xs text-[#5D4E3C]">Frage *</Label>
                <Textarea
                  id="newQ"
                  value={newQ}
                  onChange={(e) => setNewQ(e.target.value)}
                  placeholder="z. B. Worüber sollen wir als nächstes berichten?"
                  className="mt-1 rounded-none border-[#D4C4B0] resize-none"
                  rows={2}
                  maxLength={240}
                  data-testid="new-question-input"
                />
                <p className="text-[10px] text-[#A89878] mt-1">{newQ.length}/240 Zeichen</p>
              </div>
              <div>
                <Label htmlFor="newHint" className="text-xs text-[#5D4E3C]">Untertitel (optional)</Label>
                <Input
                  id="newHint"
                  value={newHint}
                  onChange={(e) => setNewHint(e.target.value)}
                  placeholder="z. B. Themenvorschläge willkommen"
                  className="mt-1 rounded-none border-[#D4C4B0]"
                  maxLength={120}
                  data-testid="new-question-hint"
                />
              </div>

              {/* Schnell-Vorlagen */}
              <div>
                <p className="text-[11px] text-[#8B7355] mb-1.5 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Schnell-Vorlagen:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATES.map((t, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { setNewQ(t.q); setNewHint(t.h); }}
                      className="text-[11px] px-2 py-1 bg-[#FAF5EE] border border-[#D4C4B0] text-[#5D4E3C] hover:bg-[#E8DCC8] transition-colors"
                      data-testid={`template-${i}`}
                    >
                      {t.q}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={!newQ.trim()}
                className="rounded-none bg-[#5D4E3C] hover:bg-[#3D2F22] text-white"
                data-testid="create-question-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Frage anlegen & aktivieren
              </Button>
              <p className="text-[10px] text-[#A89878]">
                Wird automatisch aktiviert. Andere aktive Fragen werden dabei deaktiviert.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Liste aller Fragen */}
        <Card className="rounded-none border-[#D4C4B0]">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-[#8B7355]">Lädt…</div>
            ) : questions.length === 0 ? (
              <div className="text-center py-12 text-[#8B7355]">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Noch keine Fragen angelegt</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#E8DCC8]">
                {questions.map((q) => {
                  const ansList = expandedAnswers[q.id];
                  return (
                    <li key={q.id} className="p-3 sm:p-4" data-testid={`question-row-${q.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            {q.is_active ? (
                              <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 uppercase tracking-wider">
                                Aktiv
                              </span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-700 border border-gray-200 uppercase tracking-wider">
                                Inaktiv
                              </span>
                            )}
                            <span className="text-[11px] text-[#A89878]">{formatTime(q.created_at)}</span>
                            <span className="text-[11px] text-[#8B7355] inline-flex items-center gap-1">
                              <Users className="h-3 w-3" /> {q.answer_count || 0} Antwort{(q.answer_count || 0) === 1 ? '' : 'en'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-[#5D4E3C] break-words">{q.question}</p>
                          {q.topic_hint && (
                            <p className="text-xs text-[#8B7355] mt-0.5 break-words italic">{q.topic_hint}</p>
                          )}

                          {(q.answer_count || 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => loadAnswers(q.id)}
                              className="mt-2 text-[11px] text-[#5D4E3C] underline hover:text-[#3D2F22] inline-flex items-center gap-1"
                              data-testid={`question-show-answers-${q.id}`}
                            >
                              {ansList ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              Antworten anzeigen
                            </button>
                          )}

                          {ansList && (
                            <ul className="mt-3 space-y-2 border-l-2 border-[#C4A084] pl-3">
                              {ansList.length === 0 ? (
                                <li className="text-xs text-[#A89878]">Noch keine Antworten</li>
                              ) : ansList.map((a) => (
                                <li key={a.id} className="bg-[#FAF5EE] p-2.5 border border-[#E8DCC8]">
                                  <div className="flex flex-wrap items-center gap-1.5 mb-1 text-[10px] text-[#8B7355]">
                                    <span className="font-semibold text-[#5D4E3C]">{a.author_name}</span>
                                    {a.author_email && <span>· {a.author_email}</span>}
                                    {a.is_anonymous && <span className="px-1 bg-gray-100 text-gray-600">anonym</span>}
                                    <span>· {formatTime(a.created_at)}</span>
                                  </div>
                                  <p className="text-xs text-[#5D4E3C] whitespace-pre-wrap break-words">{a.answer}</p>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div className="flex flex-col gap-1.5 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(q)}
                            className="rounded-none h-8 w-8 p-0"
                            title={q.is_active ? 'Deaktivieren' : 'Aktivieren'}
                            data-testid={`question-toggle-${q.id}`}
                          >
                            {q.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuestion(q)}
                            className="rounded-none h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                            title="Löschen"
                            data-testid={`question-delete-${q.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BlogQuestionsManager;
